import React, { useEffect, useMemo, useRef, useState } from 'react';
import './css/tag.css';
import { FavoriteHeart } from './favorite';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

const FOLDER_PREFIX = '__folder_';

const TagEdit = ({ label, onRemove, isFolder }) => {
  const display = isFolder
    ? (() => {
        const raw = label.slice(FOLDER_PREFIX.length);
        try {
          return decodeURIComponent(raw);
        } catch {
          return raw;
        }
      })()
    : label;

  return (
    <div className={`tag-chip${isFolder ? ' tag-chip--folder' : ''}`}>
      <span className="tag-chip-label">{isFolder ? `📁 ${display}` : display}</span>
      <IconButton size="small" onClick={() => onRemove(label)} aria-label="remove tag">
        <CloseIcon fontSize="small" />
      </IconButton>
    </div>
  );
};

function normalizeTagList(tag) {
  if (!tag || !String(tag).trim()) return [];
  return String(tag)
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

function letterSequenceMatch(candidate, query) {
  const hay = String(candidate || '').toLowerCase();
  const needle = String(query || '').toLowerCase().trim();
  if (!needle) return true;
  return hay.includes(needle);
}

/**
 * Tag editor with optional suggestions.
 * @param {string[]} suggestedTags — candidate tags (already ranked preferred)
 */
export const TagEditList = ({
  item,
  use_heart,
  hearts,
  onTagChange,
  suggestedTags = [],
}) => {
  const showHeart = use_heart === true || hearts === true;
  const [newTag, setNewTag] = useState('');
  const [finalTagList, setFinalTagList] = useState([]);
  const [isFavorite, setIsFavorite] = useState(false);
  const [editTags, setEditTags] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => {
    const tag = item.tag ?? item.tags;
    if (tag && String(tag).length > 0) {
      const tagList = normalizeTagList(tag);
      setIsFavorite(tagList.includes('heart'));
      setFinalTagList(tagList);
      setEditTags(tagList.filter((t) => t !== 'heart'));
    } else {
      setIsFavorite(false);
      setFinalTagList([]);
      setEditTags([]);
    }
  }, [item]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const commitTags = (tagList) => {
    setFinalTagList(tagList);
    setEditTags(tagList.filter((t) => t !== 'heart'));
    onTagChange(tagList.join(','));
  };

  const addTag = (raw) => {
    const value = String(raw || '').trim();
    if (!value) return;
    const lower = value.toLowerCase();
    if (finalTagList.some((t) => t.toLowerCase() === lower)) {
      setNewTag('');
      setShowSuggestions(false);
      return;
    }
    const tagList = [...finalTagList, value];
    commitTags(tagList);
    setNewTag('');
    setShowSuggestions(false);
    setActiveIndex(0);
  };

  const handleFavoriteChange = (nextFavorite) => {
    let tagList;
    if (nextFavorite) {
      tagList = finalTagList.includes('heart')
        ? finalTagList
        : ['heart', ...finalTagList];
    } else {
      tagList = finalTagList.filter((t) => t !== 'heart');
    }
    setIsFavorite(nextFavorite);
    commitTags(tagList);
  };

  const handleRemoveTag = (tag) => {
    const tagList = finalTagList.filter((selected) => selected !== tag);
    commitTags(tagList);
  };

  const filteredSuggestions = useMemo(() => {
    const owned = new Set(finalTagList.map((t) => t.toLowerCase()));
    const query = newTag.trim();
    return (suggestedTags || [])
      .filter((t) => t && !String(t).startsWith(FOLDER_PREFIX) && t !== 'heart')
      .filter((t) => !owned.has(String(t).toLowerCase()))
      .filter((t) => letterSequenceMatch(t, query))
      .slice(0, 10);
  }, [suggestedTags, finalTagList, newTag]);

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown' && filteredSuggestions.length) {
      event.preventDefault();
      setShowSuggestions(true);
      setActiveIndex((i) => (i + 1) % filteredSuggestions.length);
      return;
    }
    if (event.key === 'ArrowUp' && filteredSuggestions.length) {
      event.preventDefault();
      setShowSuggestions(true);
      setActiveIndex((i) =>
        i <= 0 ? filteredSuggestions.length - 1 : i - 1
      );
      return;
    }
    if (event.key === 'Escape') {
      setShowSuggestions(false);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (showSuggestions && filteredSuggestions[activeIndex]) {
        addTag(filteredSuggestions[activeIndex]);
      } else if (newTag.trim()) {
        addTag(newTag.trim());
      }
    }
  };

  return (
    <div className="tag-edit-list" ref={wrapRef}>
      {showHeart ? (
        <FavoriteHeart
          item={item}
          isFavorite={isFavorite}
          onFavoriteChange={handleFavoriteChange}
        />
      ) : null}

      {editTags.map((tag) => (
        <TagEdit
          key={tag}
          label={tag}
          isFolder={tag.startsWith(FOLDER_PREFIX)}
          onRemove={handleRemoveTag}
        />
      ))}

      <div className="tag-input-wrap">
        <input
          type="text"
          className="tag-add-input"
          placeholder="Add tag"
          value={newTag}
          onChange={(e) => {
            setNewTag(e.target.value);
            setShowSuggestions(true);
            setActiveIndex(0);
          }}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
        />
        {showSuggestions && filteredSuggestions.length > 0 && (
          <ul className="tag-suggestions" role="listbox">
            {filteredSuggestions.map((tag, index) => (
              <li
                key={tag}
                role="option"
                aria-selected={index === activeIndex}
                className={
                  index === activeIndex
                    ? 'tag-suggestion is-active'
                    : 'tag-suggestion'
                }
                onMouseDown={(e) => {
                  e.preventDefault();
                  addTag(tag);
                }}
              >
                {tag}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
